import * as PropTypes from 'prop-types';

import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {ClickableIconStyle} from 'ui-library/icons/ClickableIcon';

export const EXTRA_FIELD_PREFIX = 'extra_field:';
export const RESULT_FIELD_PREFIX = 'result_field:';
export const TABLE_KEYS = {
  PROCEDURE_METHOD_ANALYSIS_SOURCE_TABLE: 'procedureMethodAnalysisSourceTable',
  CREATE_TEST_ORDERS_TABLE: 'createTestOrdersTable',
  UPDATE_TEST_ORDER_STATUS_TABLE: 'updateTestOrderStatusTable',
  EXPANDED_BATCH_TABLE: 'expandedBatchTable',
  EXPANDED_BENCHLING_APP_TABLE: 'expandedBenchlingAppTable',
  EXPANDED_CONNECTION_TABLE: 'expandedConnectionTable',
  EXPANDED_BULK_EDIT_MODS_PREVIEW_TABLE: 'expandedBulkEditModsPreviewTable',
  EXPANDED_PROCESS_MANAGEMENT_TABLE: 'expandedProcessManagementTable',
  EXPANDED_PROJECT_FILE_TABLE: 'expandedProjectFileTable',
  EXPANDED_REGISTRY_FILE_TABLE: 'expandedRegistryFileTable',
  EXPANDED_REQUEST_TABLE: 'expandedRequestTable',
  EXPANDED_STORABLE_TABLE: 'expandedStorableTable',
  EXPANDED_WORKFLOW_TABLE: 'expandedWorkflowTable',
  EXPANDED_WORKLIST_TABLE: 'expandedWorklistTable',
  EXPANDED_ANNOTATION_TABLE: 'expandedAnnotationTable',
  EXPANDED_AA_ANNOTATION_TABLE: 'expandedAaAnnotationTable',
  EXPANDED_TRANSLATION_TABLE: 'expandedTranslationTable',
  AA_MONOMER_LISTING_TABLE: 'aaMonomerListingTable',
  AA_MONOMER_HELM_LIBRARY_TABLE: 'aaMonomerHelmLibraryTable',
  HELM_LIBRARY_MONOMER_LISTING_TABLE: 'helmLibraryMonomerListingTable',
  MONOMER_LISTING_TABLE: 'monomerListingTable',
  GRID_CELL_TABLE: 'gridCellTable',
  SAMPLE_STATUS_TABLE: 'sampleStatusTable',
  SCAN_TO_MOVE_GRID_TABLE: 'scanToMoveGridTable',
  STORABLE_SCHEMA_VALIDATION_TABLE: 'storableSchemaValidationTable',
  UNITS_ONBOARDING_WIZARD_TABLE: 'unitsOnboardingWizardTable',
  UPDATE_TEST_ORDER_CONTAINER_TABLE: 'updateTestOrderContainerTable',
  SALT_SOLVATE_LIBRARY_TABLE: 'saltSolvateLibraryTable',
};

/**
 * Table key for the expanded search results table, scoped to a sidepanel search `section` (e.g.
 * global search, registry, projects). Each section persists its own column order and hidden columns
 * under this key; callers should pass `EXPANDED_PROJECT_FILE_TABLE` as the `fallbackTableKey` so a
 * section that has no saved preference yet inherits the previously-shared value (and its defaults).
 */
export function getExpandedSearchTableKey(section: string): string {
  return `${TABLE_KEYS.EXPANDED_PROJECT_FILE_TABLE}:${section}`;
}

export enum SortDirection {
  ASCENDING_SORT = 'ascending',
  DESCENDING_SORT = 'descending',
}

export const ASCENDING_SORT = SortDirection.ASCENDING_SORT;
export const DESCENDING_SORT = SortDirection.DESCENDING_SORT;

export interface ScrollableTableColumn {
  isSortable?: boolean;
  sortKey?: string | null;
  sortDirection?: SortDirection | null;
  isCollapsible?: boolean;
  isFixed?: boolean;
  isFixedRight?: boolean;
  handleCreate?: () => void;
  icon?: BenchlingIconName;
  header: string;
  accessorKey: string;
  flexGrow?: number;
  width?: number | null;
  // When canFilterColumns is set to true on ScrollableTable, setting disableFiltering to true on a
  // specific column prevents it from being filterable (or hidden) by the user
  disableFiltering?: boolean;
  columnKey?: string;
  wrapText?: boolean;
  /**
   * When true, the cell's content is rendered inside a full-height, full-width wrapper instead of
   * the default vertically-centered, content-sized wrapper. Use this for columns whose render
   * output (e.g. an `<img>` with `width/height: 100%` and `object-fit: contain`) should fill the
   * row's body height rather than centering at its intrinsic size.
   */
  fillCell?: boolean;
}

export type CreateCustomSortElement = ({
  column,
  sortIconClassNames,
}: {
  column: ScrollableTableColumn;
  sortIconClassNames: string;
}) => JSX.Element;

export const COLUMN_HEADER_FIELDS = {
  isSortable: PropTypes.bool,
  sortKey: PropTypes.string,
  sortDirection: PropTypes.oneOf([ASCENDING_SORT, DESCENDING_SORT]),
  isCollapsible: PropTypes.bool,
  // The header cell will have a create button only if handleCreate is truthy and isSortable is false
  handleCreate: PropTypes.func,
  icon: PropTypes.string,
  header: PropTypes.string.isRequired,
};

export const COLUMN_SHAPE_FIELDS = {
  ...COLUMN_HEADER_FIELDS,
  accessorKey: PropTypes.string.isRequired,
  flexGrow: PropTypes.number,
  width: PropTypes.number,
};

export interface ScrollableTableRow {
  id: string;

  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  data: {[k: string]: any};

  // Used for row but also ScrollableTableHoverCell
  className?: string;
  // Only used for row, not ScrollableTableHoverCell (e.g. for left / right borders)
  rowOnlyClassName?: string;
  classNames?: {[k: string]: string | undefined};
  icon?: JSX.Element | string;

  isExpandable?: boolean;
  isExpanded?: boolean;
  isFaded?: boolean;
  isSelected?: boolean;
  // displayAsSelected is an override to display row as selected without affecting table selection logic or
  // styling that depends on the isSelected value (eg. in the global checkbox).
  displayAsSelected?: boolean;
  isSelectorDisabled?: boolean;

  /** @default true */
  isSelectorPresent?: boolean;
  /** If provided, a corresponding BenchlingIcon will be used for the selector rather than the default radio
   * button or checkbox input. The same select functionality will be used. */
  selectorIconProps?: {altText?: string; icon: BenchlingIconName; iconStyle: ClickableIconStyle};

  // You can extend this interface to make `extra` more type specific (e.g. `ExpandedItemTableRow`)
  extra?: object;
  tooltipText?: string;
  eventHandlers?: {
    onAnimationEnd?: () => void;
  };
}

export const ScrollableTablePreferences = {
  COLLAPSED_NAMESPACE: 'scrollableTable.collapsed_',
  HIDDEN_COLUMN_NAMESPACE: '.hiddenColumns',
  COLUMN_ORDER_NAMESPACE: '.columnOrder',
};

export enum RowSelectionMode {
  SINGLE = 'SINGLE',
  MULTIPLE = 'MULTIPLE',
}

// Keep in sync with values in `less/components/scrollable-table.less`.
export const TABLE_ROW_BODY_HEIGHT = 20; // @height--scrollableTableRowBody
const TABLE_HEADER_BORDER_SIZE = 1; // @border--scrollableTableHeader
const TABLE_HEADER_BODY_HEIGHT = 16; // @height--scrollableTableHeaderBody
const TABLE_HEADER_PADDING_VERTICAL = 10; // @paddingVertical--scrollableTableHeader
export const TABLE_BODY_BORDER_SIZE = 1; // @border--scrollableTableBody
export const COMPACT_TABLE_PADDING_VERTICAL = 6; // @paddingVertical--scrollableTable
export const TABLE_PADDING_VERTICAL = 12;
export const TABLE_HEADER_HEIGHT =
  TABLE_HEADER_BODY_HEIGHT + TABLE_HEADER_PADDING_VERTICAL * 2 + TABLE_HEADER_BORDER_SIZE;

// these are all properties of fixed-data-table-2
export const VERTICAL_SCROLLBAR_WIDTH = 15;
export const HORIZONTAL_SCROLLBAR_HEIGHT = 16;
